import { useState, useRef, useEffect } from 'react'
import {
  Rocket,
  Bot,
  Calendar,
  ChevronLeft,
  ChevronRight,
  Gift,
  History,
  LineChart,
  LogOut,
  Radio,
  Phone,
  PhoneCall,
  PhoneOutgoing,
  Settings,
  Smartphone,
  User,
  Zap,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router'
import { cn } from '@/lib/utils'
import { useUIStore } from '@/stores/useUIStore'
import { useProfile } from '@/api/profile'
import { useSubscriptionStatus } from '@/api/subscription'
import { logout } from '@/lib/auth'
import { OutilsPerso } from '@/components/dashboard/OutilsPerso'
import { CarteEssai } from '@/components/CarteEssai'
import { SETTINGS_DEFAULT_SECTION, SETTINGS_SECTION_IDS } from '@/components/manager/SettingsView'
import { useAccess } from '@/hooks/useAccess'
import { usePartnerSpace } from '@/api/partenaires'

interface NavItem {
  id: string
  icon: typeof PhoneCall
  label: string
  badge?: { text: string; tone: 'green' | 'orange' | 'violet' }
}

interface Props {
  activeTab: string
  onTabChange: (tab: string) => void
}

export function ManagerSidebar({ activeTab, onTabChange }: Props) {
  const { t } = useTranslation()
  const { locale = 'fr' } = useParams<{ locale: string }>()
  const navigate = useNavigate()
  const { darkMode, sidebarCollapsed, toggleSidebarCollapsed, toggleDarkMode } =
    useUIStore()
  const { data: profile } = useProfile()
  const { data: abonnement } = useSubscriptionStatus()
  const enEssai = abonnement?.subscription?.status === 'trialing'
  const access = useAccess()

  // Barre dense, par groupes (modèle des concurrents) : tout tient sans défiler.
  const tousLesGroupes: { titre?: string; items: NavItem[] }[] = [
    { items: [
      // « Commencer » n'existe que pendant l'essai (pas à faire, minutes restantes).
      ...(enEssai ? [{ id: 'overview', icon: Rocket, label: t('manager.tabs.start', 'Commencer') }] : []),
      // Le numéro est le centre de l'administration : tout ce qui le concerne se règle sur sa page.
      { id: 'numbers', icon: PhoneOutgoing, label: t('manager.tabs.numbers', 'Numéros') },
    ] },
    { titre: t('manager.groups.answer', 'Répondre'), items: [
      { id: 'ai-agents', icon: Bot, label: t('manager.tabs.aiAgents', 'Agents IA') },
      { id: 'calendars', icon: Calendar, label: t('manager.tabs.calendars', 'Calendriers') },
    ] },
    { titre: t('manager.groups.call', 'Appeler'), items: [
      { id: 'batch-calls', icon: PhoneOutgoing, label: t('manager.tabs.batchCalls', 'Campagnes') },
      { id: 'power-dialer', icon: Zap, label: 'Power Dialer' },
    ] },
    { titre: t('manager.groups.track', 'Suivre'), items: [
      { id: 'live', icon: Radio, label: t('manager.tabs.live', 'Live') },
      { id: 'analytics', icon: LineChart, label: t('manager.tabs.analytics', 'Analytics') },
      { id: 'history', icon: History, label: t('manager.tabs.history', 'Historique') },
    ] },
    // Réglages : on y va une fois, puis rarement. Replié par défaut, ouvert
    // tant qu'on est sur une de ses pages.
    // Réglages : page à part avec sa propre colonne (SettingsView).
    { items: [
      { id: 'settings', icon: Settings, label: t('manager.tabs.settings', 'Réglages') },
    ] },
  ]

  // A supervisor follows the team and sets nothing: the « Follow » group only.
  const groupes = access.isSupervisor
    ? tousLesGroupes.filter((g) => g.items.some((i) => i.id === 'analytics'))
    : tousLesGroupes

  // « Recommander Callizy » sits in the name menu and opens the partner section of Settings.
  const partnerVisible = (usePartnerSpace().data?.visible ?? false) && !access.isSupervisor
  const surReglages = SETTINGS_SECTION_IDS.includes(activeTab)

  const sidebarBg = darkMode ? '#1e293b' : '#ffffff'
  const borderColor = darkMode ? 'rgb(30, 41, 59)' : 'rgb(226, 232, 240)'

  return (
    <aside
      className={cn(
        'hidden md:flex h-screen flex-col border-r transition-all duration-200',
        sidebarCollapsed ? 'w-[68px]' : 'w-64',
        darkMode ? 'text-slate-300' : 'text-slate-700',
      )}
      style={{ backgroundColor: sidebarBg, borderColor }}
    >
      {/* Header */}
      {sidebarCollapsed ? (
        <div className="p-3 pb-2 flex flex-col items-center gap-2">
          <div
            className={cn(
              'w-10 h-10 rounded-xl flex items-center justify-center overflow-hidden flex-shrink-0',
              darkMode ? 'bg-white' : 'bg-slate-900',
            )}
          >
            <img
              src="https://sdk.callizy.ca/assets/favicon_callizy.png"
              alt="Callizy"
              className="object-contain w-full h-full"
            />
          </div>
          <button
            type="button"
            onClick={toggleSidebarCollapsed}
            title={t('sidebar.expand', 'Déplier la barre latérale')}
            className={cn(
              'w-7 h-7 rounded-md flex items-center justify-center transition-colors flex-shrink-0',
              darkMode
                ? 'bg-slate-800 text-slate-300 hover:bg-slate-700 hover:text-white'
                : 'bg-slate-100 text-slate-500 hover:bg-slate-200 hover:text-slate-900',
            )}
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      ) : (
        <div className="flex items-center gap-2.5 px-4 pt-4 pb-3">
          <div
            className={cn(
              'w-8 h-8 rounded-lg flex items-center justify-center overflow-hidden flex-shrink-0',
              darkMode ? 'bg-white' : 'bg-slate-900',
            )}
          >
            <img
              src="https://sdk.callizy.ca/assets/favicon_callizy.png"
              alt="Callizy"
              className="object-contain w-full h-full"
            />
          </div>
          <span
            className={cn(
              'font-bold text-lg tracking-tight flex-1',
              darkMode ? 'text-white' : 'text-slate-900',
            )}
          >
            Callizy
          </span>
          <button
            type="button"
            onClick={toggleSidebarCollapsed}
            title={t('sidebar.collapse', 'Replier la barre latérale')}
            className={cn(
              'p-1.5 rounded-lg transition-colors',
              darkMode
                ? 'text-slate-400 hover:text-white hover:bg-slate-800'
                : 'text-slate-400 hover:text-slate-900 hover:bg-slate-100',
            )}
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Nav items */}
      <nav className="flex-1 px-2 overflow-y-auto min-h-0 space-y-1">
        {groupes.map((groupe, gi) => (
          <div key={gi} className="space-y-px">
            {groupe.titre && !sidebarCollapsed && (
              <p className={cn('px-3 pt-1.5 pb-0.5 text-[10.5px] font-semibold uppercase tracking-wider', darkMode ? 'text-slate-500' : 'text-slate-400')}>
                {groupe.titre}
              </p>
            )}
            {groupe.titre && sidebarCollapsed && <div className={cn('mx-3 my-1 h-px', darkMode ? 'bg-slate-800' : 'bg-slate-200')} />}
            {groupe.items.map((item) => {
              const Icon = item.icon
              const isActive = activeTab === item.id || (item.id === 'settings' && surReglages)
                || (item.id === 'ai-agents' && activeTab === 'connected-software')
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => onTabChange(item.id === 'settings' ? SETTINGS_DEFAULT_SECTION : item.id)}
                  className={cn(
                    'w-full flex items-center h-7 rounded-md text-[13px] transition-colors',
                    sidebarCollapsed ? 'justify-center px-2' : 'gap-2.5 px-3',
                    isActive
                      ? darkMode
                        ? 'bg-slate-800 text-white font-medium'
                        : 'bg-slate-100 text-slate-900 font-medium'
                      : darkMode
                        ? 'text-slate-300 hover:bg-slate-800 hover:text-white'
                        : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900',
                  )}
                  title={sidebarCollapsed ? item.label : undefined}
                >
                  <Icon className="h-4 w-4 flex-shrink-0" />
                  {!sidebarCollapsed && (
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <span className="flex-1 text-left truncate">{item.label}</span>
                      {item.badge && (
                        <span
                          className={cn(
                            'px-1.5 py-0.5 text-[10px] leading-none font-bold text-white rounded-full',
                            item.badge.tone === 'green'
                              ? 'bg-emerald-500'
                              : item.badge.tone === 'orange'
                                ? 'bg-orange-500'
                                : 'bg-violet-500',
                          )}
                        >
                          {item.badge.text}
                        </span>
                      )}
                    </div>
                  )}
                </button>
              )
            })}
          </div>
        ))}
      </nav>

      <CarteEssai darkMode={darkMode} collapsed={sidebarCollapsed} className="mx-2 mb-1" />

      {/* Footer */}
      <div
        className={cn(
          'p-2 pt-2 border-t space-y-0.5 flex-shrink-0',
          darkMode ? 'border-slate-800' : 'border-slate-200',
        )}
      >
        {/* My phone — bouton primaire plein pour bascule softphone */}
        <button
          type="button"
          onClick={() => navigate(`/${locale}/dashboard`)}
          title={t('manager.myPhone', 'Téléphone')}
          className={cn(
            'w-full flex items-center rounded-lg font-semibold transition-colors shadow-sm',
            sidebarCollapsed ? 'justify-center p-2' : 'gap-2.5 px-3 h-9',
            darkMode
              ? 'bg-indigo-600 text-white hover:bg-indigo-500'
              : 'bg-[#0A192F] text-white hover:bg-[#0A192F]/90',
          )}
        >
          <Phone className="h-4 w-4 flex-shrink-0" />
          {!sidebarCollapsed && (
            <span className="text-[13.5px]">{t('manager.myPhone', 'Téléphone')}</span>
          )}
        </button>

        <ManagerProfilePopover
          collapsed={sidebarCollapsed}
          firstName={profile?.first_name || ''}
          lastName={profile?.last_name || ''}
          photo={profile?.avatar_url || ''}
          role={t(`recordingAccess.roles.${access.role}.label`)}
          withBilling={access.canBilling}
          darkMode={darkMode}
          onNavigateToProfile={() =>
            access.isSupervisor ? navigate(`/${locale}/dashboard?view=profile`) : onTabChange('profile')
          }
          onRecommend={partnerVisible ? () => onTabChange('partner') : undefined}
        />
        <OutilsPerso darkMode={darkMode} collapsed={sidebarCollapsed} onToggleDarkMode={toggleDarkMode} />
      </div>
    </aside>
  )
}

interface ProfilePopoverProps {
  collapsed: boolean
  firstName: string
  photo?: string
  lastName: string
  role: string
  /** « Profile and billing » for the owner, « Profile » for everyone else. */
  withBilling?: boolean
  darkMode: boolean
  onNavigateToProfile?: () => void
  /** « Recommander Callizy », shown only to a partner or once the program is open. */
  onRecommend?: () => void
}

function ManagerProfilePopover({
  collapsed,
  firstName,
  lastName,
  role,
  withBilling = true,
  darkMode,
  onNavigateToProfile,
  onRecommend,
  photo,
}: ProfilePopoverProps) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const { t } = useTranslation()
  const { locale = 'fr' } = useParams<{ locale: string }>()

  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  const initials = `${firstName[0] || ''}${lastName[0] || ''}`.toUpperCase() || 'M'

  if (collapsed) {
    return (
      <button
        type="button"
        onClick={() => logout()}
        title={t('sidebar.logout', 'Déconnexion')}
        className={cn(
          'w-full flex items-center justify-center py-2 rounded-xl transition-colors',
          darkMode ? 'text-rose-400 hover:bg-rose-500/10' : 'text-rose-500 hover:bg-rose-50',
        )}
      >
        <LogOut className="h-5 w-5" />
      </button>
    )
  }

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={cn(
          'w-full flex items-center gap-2.5 px-2 h-10 rounded-lg transition-colors',
          darkMode ? 'hover:bg-slate-800' : 'hover:bg-slate-50',
        )}
      >
        {photo ? (
          <img src={photo} alt="" className="w-9 h-9 rounded-full object-cover flex-shrink-0" />
        ) : (
          <div
            className={cn(
              'w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0',
              darkMode ? 'bg-slate-700 text-slate-200' : 'bg-slate-200 text-slate-700',
            )}
          >
            {initials}
          </div>
        )}
        <div className="flex-1 min-w-0 text-left">
          <p
            className={cn(
              'text-sm font-semibold truncate',
              darkMode ? 'text-white' : 'text-slate-900',
            )}
          >
            {firstName} {lastName}
          </p>
          <p
            className={cn(
              'text-[11px] truncate',
              darkMode ? 'text-slate-400' : 'text-slate-500',
            )}
          >
            {role}
          </p>
        </div>
      </button>
      {open && (
        <div
          className={cn(
            'absolute bottom-full left-0 right-0 mb-1 rounded-xl border shadow-lg overflow-hidden z-50',
            darkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200',
          )}
        >
          <button
            type="button"
            onClick={() => {
              setOpen(false)
              onNavigateToProfile?.()
            }}
            className={cn(
              'w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors',
              darkMode
                ? 'text-slate-200 hover:bg-slate-700'
                : 'text-slate-700 hover:bg-slate-50',
            )}
          >
            <User className="w-4 h-4" />
            <span>{withBilling ? t('sidebar.profileAndBilling', 'Profil et facturation') : t('manager.tabs.profile', 'Profil')}</span>
          </button>
          {onRecommend && (
            <button
              type="button"
              onClick={() => {
                setOpen(false)
                onRecommend()
              }}
              className={cn(
                'w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors',
                darkMode ? 'text-slate-200 hover:bg-slate-700' : 'text-slate-700 hover:bg-slate-50',
              )}
            >
              <Gift className="w-4 h-4" />
              <span>{t('partner.sidebar', 'Recommander Callizy')}</span>
            </button>
          )}
          <a
            href={`https://callizy.ca/${locale}/app`}
            target="_blank"
            rel="noreferrer"
            onClick={() => setOpen(false)}
            className={cn(
              'w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors',
              darkMode ? 'text-slate-200 hover:bg-slate-700' : 'text-slate-700 hover:bg-slate-50',
            )}
          >
            <Smartphone className="w-4 h-4" />
            <span>{t('sidebar.getApp', "Télécharger l'app")}</span>
          </a>
          <div className={cn('h-px', darkMode ? 'bg-slate-700' : 'bg-slate-100')} />
          <button
            type="button"
            onClick={() => {
              setOpen(false)
              logout()
            }}
            className={cn(
              'w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors',
              darkMode
                ? 'text-rose-400 hover:bg-rose-500/10'
                : 'text-rose-500 hover:bg-rose-50',
            )}
          >
            <LogOut className="w-4 h-4" />
            <span>{t('sidebar.logout', 'Déconnexion')}</span>
          </button>
        </div>
      )}
    </div>
  )
}
